
/**
 * GSC DSP KERNEL - V150.1 (ANTI-REGRESSION TIMING PATCH)
 * Authority: Senior Reliability Engineer
 */

export interface FrequencyBands { low: number; mid: number; high: number; }
export interface AnalyzeFullPayload {
    stuData: Float32Array | number[] | ArrayBufferLike;
    refData: Float32Array | number[] | ArrayBufferLike | null;
    sampleRate: number;
    targetBpm?: number;
    hardwareLagMs?: number; // Concept séparé
}

export interface OnsetMetadata {
    time: number;
    intensity: number;
}

export interface AnalyzeFullResult {
    chroma: number[]; 
    chromaHistory: number[][];
    refChromaHistory?: number[][]; 
    chroma_entropy: number;
    chroma_clarity: number;
    onset_count: number;
    onsetTimesMs: number[]; 
    onsetIntensities: number[]; 
    refOnsetTimesMs: number[];   
    refOnsetIntensities: number[]; 
    timingDeviationMs: number[];
    rms_db: number;
    confidence: number;
    stability: number; 
    transient_ratio: number; 
    peakedness: number;      
    stuEnvelope: number[];
    refEnvelope: number[];
    stuBands: FrequencyBands;
    refBands: FrequencyBands;
    stuFingerprint: number[];
    refFingerprint: number[];
    targetBpm: number;
    sampleRate: number;
    noise_ratio: number;
    clip_ratio: number;
    ghostNotes: any[];
    chroma_clarity_score: number;
    hardwareLagMs: number;
}

function computeEnergyBands(data: Float32Array): FrequencyBands {
    if (!data || data.length === 0) return { low: 0.33, mid: 0.33, high: 0.33 };
    let low = 0, mid = 0, high = 0;
    const step = 4;
    for (let i = step; i < data.length - step; i += step) {
        const delta = Math.abs(data[i] - data[i - step]);
        const val = Math.abs(data[i]);
        if (delta > val * 0.8) high += val;
        else if (delta > val * 0.2) mid += val;
        else low += val;
    }
    const total = (low + mid + high) || 1;
    return { low: low / total, mid: mid / total, high: high / total };
}

function computeSpectralFingerprint(data: Float32Array): number[] {
    if (!data || data.length < 4096) return [];
    const fingerprint: number[] = [];
    const windowSize = 2048;
    const hopSize = 1024;
    for (let i = 0; i < data.length - windowSize; i += hopSize) {
        let energy = 0;
        let diffSum = 0;
        for (let j = 0; j < windowSize; j++) {
            const v = data[i + j]; energy += v * v;
            if (j > 0) diffSum += Math.abs(v - data[i + j - 1]);
        }
        const brightness = energy > 1e-9 ? (diffSum / energy) * 10 : 0;
        fingerprint.push(brightness);
    }
    const maxVal = Math.max(...fingerprint, 0.001);
    return fingerprint.map(v => (v / maxVal) * 100);
}

function computeChroma(frame: Float32Array, sr: number): number[] {
    const chroma = new Float32Array(12);
    const freqs = [16.35, 17.32, 18.35, 19.45, 20.60, 21.83, 23.12, 24.50, 25.96, 27.50, 29.14, 30.87];
    for (let n = 0; n < 12; n++) {
        let noteE = 0;
        for (let oct = 2; oct < 7; oct++) {
            const f = freqs[n] * Math.pow(2, oct);
            const omega = 2 * Math.PI * f / sr;
            let s = 0, c = 0;
            for (let i = 0; i < frame.length; i++) {
                s += frame[i] * Math.sin(omega * i);
                c += frame[i] * Math.cos(omega * i);
            }
            noteE += Math.sqrt(s * s + c * c);
        }
        chroma[n] = noteE;
    }
    const max = Math.max(...Array.from(chroma)) || 0.001;
    return Array.from(chroma).map(v => v / max);
}

function calculateStability(onsets: number[]): number {
    if (onsets.length < 3) return 0.5;
    const intervals = [];
    for (let i = 1; i < onsets.length; i++) {
        const diff = onsets[i] - onsets[i - 1];
        if (diff > 0) intervals.push(diff);
    }
    const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const variance = intervals.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / intervals.length;
    return Math.max(0, Math.min(1, 1 - (Math.sqrt(variance) / (mean || 1))));
}

export function detectDetailedOnsets(data: Float32Array, sampleRate: number): OnsetMetadata[] {
    const onsets: OnsetMetadata[] = [];
    const hopSize = 256;
    const windowSize = 512;
    if (data.length < windowSize) return [];
    const energies: number[] = [];
    for (let i = 0; i < data.length - windowSize; i += hopSize) {
        let s = 0; for (let j = 0; j < windowSize; j++) s += data[i + j] * data[i + j];
        energies.push(Math.sqrt(s / windowSize));
    }
    
    const HARD_ENERGY_THRESHOLD = 0.025; 
    const SLOPE_FACTOR = 1.6;

    for (let i = 2; i < energies.length; i++) {
        if (energies[i] > energies[i-1] * SLOPE_FACTOR && energies[i] > HARD_ENERGY_THRESHOLD) {
            onsets.push({
                time: (i * hopSize) / sampleRate,
                intensity: energies[i]
            });
            i += Math.floor(sampleRate * 0.12 / hopSize); 
        }
    }
    return onsets;
}

export async function computeArticulation(data: Float32Array, sampleRate: number): Promise<{ onset_count: number; transient_ratio: number; confidence: number }> {
    const onsets = detectDetailedOnsets(data, sampleRate);
    const onsetIntensities = onsets.map(o => o.intensity);
    
    let sumSqTotal = 0;
    for (let k = 0; k < data.length; k++) {
        const v = data[k];
        sumSqTotal += v * v;
    }
    
    const avgPeak = onsetIntensities.length > 0 ? onsetIntensities.reduce((a, b) => a + b, 0) / onsetIntensities.length : 0.1;
    const tr = Math.min(1.0, (avgPeak / (Math.sqrt(sumSqTotal / (data.length || 1)) + 0.01)) / 1.4);
    
    return {
        onset_count: onsets.length,
        transient_ratio: tr,
        confidence: onsets.length > 0 ? 0.95 : 0.1
    };
}

export async function computeFullAnalysis(payload: AnalyzeFullPayload): Promise<AnalyzeFullResult> {
    const stu = payload.stuData instanceof Float32Array ? payload.stuData : new Float32Array(payload.stuData as any);
    const ref = payload.refData ? (payload.refData instanceof Float32Array ? payload.refData : new Float32Array(payload.refData as any)) : null;
    const sr = payload.sampleRate || 44100;
    const bpm = payload.targetBpm || 120;
    
    // --- TIMING SANITY GATE ---
    let hLag = payload.hardwareLagMs || 0;
    if (!Number.isFinite(hLag) || hLag > 2000) {
        console.error("[TIMING_SANITY_FAIL] Abnormal hardware lag detected:", hLag);
        hLag = 0;
    }
    const safeHardwareLagMs = Math.min(800, Math.max(0, hLag));

    const stuDetailedOnsets = detectDetailedOnsets(stu, sr);
    const stuOnsets = stuDetailedOnsets.map(o => o.time);
    const stuIntensities = stuDetailedOnsets.map(o => Math.min(1.0, o.intensity * 2.5));

    const chrHist: number[][] = [];
    let clarSum = 0;
    const win = 4096;
    for (let i = 0; i < stu.length - win; i += win) {
        const c = computeChroma(stu.slice(i, i + win), sr);
        chrHist.push(c);
        const sorted = [...c].sort((a, b) => b - a);
        clarSum += (sorted[0] - sorted[1]);
    }
    const clarity = chrHist.length > 0 ? Math.min(1, (clarSum / chrHist.length) * 1.2) : 0;

    const stuBands = computeEnergyBands(stu);
    const refBands = ref ? computeEnergyBands(ref) : { low: 0.33, mid: 0.33, high: 0.33 };
    const stuFingerprint = computeSpectralFingerprint(stu);
    const refFingerprint = ref ? computeSpectralFingerprint(ref) : [];

    const computeEnv = (data: Float32Array) => {
        const env = []; const hop = 512;
        for(let i=0; i<data.length; i+=hop) {
            let s=0; for(let j=0; j<hop && i+j<data.length; j++) s+=data[i+j]*data[i+j];
            env.push(Math.sqrt(s/hop));
        }
        return env;
    };

    const stuEnv = computeEnv(stu);
    let refEnv: number[] = [];
    let refOnsets: number[] = [];
    let refIntensities: number[] = [];
    let refChrHist: number[][] = [];

    if (ref && ref.length > 100) {
        const refDetailedOnsets = detectDetailedOnsets(ref, sr);
        refOnsets = refDetailedOnsets.map(o => o.time);
        refIntensities = refDetailedOnsets.map(o => Math.min(1.0, o.intensity * 2.5));
        refEnv = computeEnv(ref);
        for (let i = 0; i < ref.length - win; i += win) {
            refChrHist.push(computeChroma(ref.slice(i, i + win), sr));
        }
    } else {
        const secPerBeat = 60 / bpm;
        const totalDuration = stu.length / sr;
        refEnv = new Array(stuEnv.length).fill(0.02);
        for (let t = 0; t < totalDuration; t += secPerBeat) {
            refOnsets.push(t);
            refIntensities.push(0.5); 
        }
    }

    // ONSET ALIGNMENT: Uniquement hardware lag ms. startOffsetMs est géré par la couche d'orchestration pour l'alignement de fichiers.
    const timingDeviationMs = stuOnsets.map(sT => {
        if (refOnsets.length === 0) return 0;
        let minDelta = Infinity;
        const compensatedStuT = sT - (safeHardwareLagMs / 1000);

        for (const rT of refOnsets) {
            const delta = (compensatedStuT - rT) * 1000;
            if (Math.abs(delta) < Math.abs(minDelta)) minDelta = delta;
        }
        return Math.abs(minDelta) < 600 ? minDelta : 0;
    });

    let sumSqTotal = 0; let clipCount = 0;
    for (let k = 0; k < stu.length; k++) {
        const v = Math.abs(stu[k]); sumSqTotal += v * v; if (v >= 0.98) clipCount++;
    }
    const rms_db = stu.length > 0 ? 10 * Math.log10(sumSqTotal / stu.length + 1e-24) : -240;

    const avgPeak = stuIntensities.length > 0 ? stuIntensities.reduce((a,b)=>a+b,0)/stuIntensities.length : 0.1;
    const tr = Math.min(1.0, (avgPeak / (Math.sqrt(sumSqTotal/(stu.length||1)) + 0.01)) / 1.4);

    return {
        chroma: chrHist[0] || new Array(12).fill(0),
        chromaHistory: chrHist,
        refChromaHistory: refChrHist,
        chroma_entropy: 0.5,
        chroma_clarity: clarity,
        chroma_clarity_score: Math.round(clarity * 100),
        onset_count: stuOnsets.length,
        onsetTimesMs: stuOnsets.map(t => t * 1000),
        onsetIntensities: stuIntensities,
        refOnsetTimesMs: refOnsets.map(t => t * 1000),
        refOnsetIntensities: refIntensities,
        timingDeviationMs,
        rms_db,
        confidence: stuOnsets.length > 0 ? 0.95 : 0.1,
        stability: calculateStability(stuOnsets),
        transient_ratio: tr,
        peakedness: 0.6,
        stuEnvelope: stuEnv,
        refEnvelope: refEnv,
        stuBands,
        refBands,
        stuFingerprint,
        refFingerprint,
        targetBpm: bpm,
        sampleRate: sr,
        noise_ratio: 0,
        clip_ratio: clipCount / (stu.length || 1),
        ghostNotes: [],
        hardwareLagMs: safeHardwareLagMs
    };
}
